import { Fragment, type ReactNode } from 'react';

const isMergeableSeparator = (separator: string) =>
  /^\s*[-\u00AD\u2010\u2011]?\s*$/u.test(separator);

const isBoundaryPunctuation = (character: string) =>
  '.,!?;:"“”‘’()[]{}…'.includes(character);

type SelectionRangeLike = { start?: number; length?: number };

type HighlightedSelectionsProps = {
  text: string;
  selections: SelectionRangeLike[];
  /** Replaces the default mark, e.g. to make every highlight a link. */
  renderHighlight?: (highlightedText: string) => ReactNode;
};

export const highlightMarkClassName = 'rounded-sm bg-amber-100 font-semibold text-amber-700';

const getMergedSelections = (text: string, selections: SelectionRangeLike[]) => {
  const mergedSelections: Array<{ start: number; length: number }> = [];
  let selectionEnd = 0;

  [...selections]
    .sort((left, right) => (left.start ?? Number.MAX_SAFE_INTEGER) - (right.start ?? Number.MAX_SAFE_INTEGER))
    .forEach((selection) => {
      let start = selection.start;
      const length = selection.length;
      if (
        start === undefined ||
        length === undefined ||
        start < selectionEnd ||
        length <= 0 ||
        start + length > text.length
      ) {
        return;
      }

      let end = start + length;
      while (start < end && isBoundaryPunctuation(text[start])) {
        start++;
      }
      while (end > start && isBoundaryPunctuation(text[end - 1])) {
        end--;
      }
      if (start === end || start < selectionEnd) {
        return;
      }

      const gap = text.slice(selectionEnd, start);
      if (mergedSelections.length > 0 && isMergeableSeparator(gap)) {
        const previous = mergedSelections[mergedSelections.length - 1];
        previous.length = end - previous.start;
      } else {
        mergedSelections.push({ start, length: end - start });
      }

      selectionEnd = end;
    });

  return mergedSelections;
};

export function replaceSelectionsWithCloze(
  text: string,
  selections: SelectionRangeLike[],
  cloze = '______',
) {
  const mergedSelections = getMergedSelections(text, selections);
  if (mergedSelections.length === 0) return text;

  let result = '';
  let cursor = 0;

  mergedSelections.forEach(({ start, length }) => {
    result += text.slice(cursor, start);
    result += cloze;
    cursor = start + length;
  });

  return result + text.slice(cursor);
}

export function HighlightedSelections({
  text,
  selections,
  renderHighlight,
}: HighlightedSelectionsProps) {
  const parts: ReactNode[] = [];
  const mergedSelections = getMergedSelections(text, selections);

  let cursor = 0;

  mergedSelections.forEach((selection, index) => {
    const { start, length } = selection;

    if (start > cursor) {
      parts.push(<span key={`plain-${cursor}`}>{text.slice(cursor, start)}</span>);
    }
    const highlightedText = text.slice(start, start + length);
    parts.push(
      renderHighlight ? (
        <Fragment key={`selection-${start}-${index}`}>{renderHighlight(highlightedText)}</Fragment>
      ) : (
        <span key={`selection-${start}-${index}`} className={highlightMarkClassName}>
          {highlightedText}
        </span>
      )
    );
    cursor = start + length;
  });

  if (cursor < text.length || parts.length === 0) {
    parts.push(<span key={`plain-${cursor}`}>{text.slice(cursor)}</span>);
  }

  return <>{parts}</>;
}
